/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer, useState } from "react";
import { Else, If, Then, When } from "react-if";
import { Button, Message } from "@nordcloud/gnui";
import { isNonEmpty } from "~/tools";
import {
  getFlatPlanEntities,
  isBaselinePatchingEnabled,
  isBelowBaselineSourceEntity,
  isPatchOrScanEntity,
} from "~/utils";
import { baselineStrings } from "~/views/plans/constants";
import { usePlanWizard } from "../PlanProvider";
import { StepState } from "../types";
import { ActionsView, NoActionView } from "./components";
import { AddActionWrapper } from "./components/AddAction/AddActionWrapper";
import { StepStateCtxProvider } from "./StepProvider";

type Props = {
  nextStep?: () => void;
};

export function PlanSettingsForm({ nextStep }: Props) {
  const { planData, selectedEntity } = usePlanWizard();
  const [showBaselineWarningMessage, setShowBaselineWarningMessage] =
    useState(true);

  const planDataEntities = planData?.plan_settings?.planEntities ?? [];

  const flatWithBatches = planDataEntities.flatMap((entity) => {
    if (entity.batchActions) {
      return [entity, ...entity.batchActions];
    }
    return entity;
  });

  const selectedEntityDefault =
    flatWithBatches.find((entity) => entity.listId === selectedEntity) ??
    flatWithBatches[0];

  const [stepState, updateStepState] = useReducer(
    (data: StepState, partialData: Partial<StepState>) => ({
      ...data,
      ...partialData,
    }),
    {
      entities: planDataEntities,
      selectedEntity: selectedEntityDefault,
    }
  );

  const areEntities = isNonEmpty(stepState.entities);

  const showBaselineWarning = () => {
    if (!showBaselineWarningMessage) {
      return false;
    }

    const flatEntities = getFlatPlanEntities(planDataEntities);
    const patchOrScanActions = flatEntities.filter((entity) =>
      isPatchOrScanEntity(entity)
    );

    const hasActionsBelowBaseline = patchOrScanActions.some((entity) =>
      isBelowBaselineSourceEntity(entity, patchOrScanActions)
    );

    return (
      isBaselinePatchingEnabled(patchOrScanActions) && !hasActionsBelowBaseline
    );
  };

  return (
    <StepStateCtxProvider value={{ stepState, updateStepState }}>
      <When condition={showBaselineWarning()}>
        <Message
          status="notification"
          image="info"
          css={{ marginBottom: "16px" }}
        >
          {baselineStrings.singleBaselineActionWarning}
          <Button
            size="sm"
            severity="low"
            icon="close"
            css={{ marginLeft: "auto" }}
            onClick={() => setShowBaselineWarningMessage(false)}
          />
        </Message>
      </When>
      <If condition={areEntities}>
        <Then>
          <ActionsView nextStep={nextStep} />
        </Then>
        <Else>
          <NoActionView addAction={<AddActionWrapper />} />
        </Else>
      </If>
    </StepStateCtxProvider>
  );
}
